/**
 * @file components/chat/CompactModelSelector.jsx
 * Uses useChatSession
 */
import React, { useState, useEffect, useRef } from 'react';
import { useChatSession } from '../../context/ChatSessionContext';
import { PROPRIETARY_MODELS, PROVIDER_GROUPS, OTHER_GROUP_LABEL } from '../../config';
import styles from '../../styles/ChatApp.module.css';
import ModelIcon from '../common/ModelIcon';
import { ChevronDown } from 'lucide-react';

// Build the same grouped structure used by WelcomeModelSelector so the two
// selectors always stay in sync when models are added to config.js.
// Collect all models explicitly listed in any group (supports both {prefix} and {models} shapes)
const claimedByPrefix = new Set(
    PROVIDER_GROUPS.flatMap(({ prefix, models }) =>
        models ? models : (prefix ? PROPRIETARY_MODELS.filter(m => m.startsWith(prefix)) : [])
    )
);

const GROUPED_MODELS = [
    ...PROVIDER_GROUPS.map(({ label, prefix, models }) => ({
        label,
        models: models ?? PROPRIETARY_MODELS.filter(m => m.startsWith(prefix)),
    })),
    {
        label: OTHER_GROUP_LABEL,
        models: PROPRIETARY_MODELS.filter(m => !claimedByPrefix.has(m)),
    },
].filter(g => g.models.length > 0);

const CompactModelSelector = () => {
    const { selectedModel, setSelectedModel } = useChatSession();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (model) => {
        setSelectedModel(model);
        setIsOpen(false);
    };

    return (
        <div className={styles.compactSelectorWrapper} ref={menuRef}>
            <button
                type="button"
                className={styles.compactSelectorButton}
                onClick={() => setIsOpen(prev => !prev)}
            >
                <ModelIcon modelName={selectedModel} size={16} />
                <span>{selectedModel}</span>
                <ChevronDown
                    size={16}
                    className={`${styles.compactSelectorArrow} ${isOpen ? styles.open : ''}`}
                />
            </button>

            {isOpen && (
                <div className={styles.modelPopupMenu}>
                    {/* Route AI sits above the groups as a standalone entry */}
                    <div
                        className={`${styles.modelPopupItem} ${selectedModel === 'Route AI' ? styles.activePopupItem : ''}`}
                        onClick={() => handleSelect('Route AI')}
                    >
                        <ModelIcon modelName="Route AI" size={16} />
                        <span>Route AI</span>
                    </div>

                    {/* Provider groups with divider labels */}
                    {GROUPED_MODELS.map(({ label, models }) => (
                        <React.Fragment key={label}>
                            <div className={styles.modelPopupGroupLabel}>{label}</div>
                            {models.map(model => (
                                <div
                                    key={model}
                                    className={`${styles.modelPopupItem} ${selectedModel === model ? styles.activePopupItem : ''}`}
                                    onClick={() => handleSelect(model)}
                                >
                                    <ModelIcon modelName={model} size={16} />
                                    <span>{model}</span>
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CompactModelSelector;
